import * as React from 'react'
import { Close } from 'components/icons'
import { Input } from 'components/input'
import { Button } from 'components/button'
import { AppStore } from 'stores/app-store'
import { UiStore } from 'stores/ui-store'
import { DashboardUsersStore } from '../user-store'
import { inject, observer } from 'mobx-react'
import { IUserApi } from 'interfaces/api/portal/user-api'
import { Loader } from 'components/loader'
import { ModalLayerStore } from 'stores/modal-layer-store'
import { AuthStore } from 'stores/auth-store'
import { withTranslation, WithTranslation } from 'react-i18next'
import style from './clone-user-modal.css'
import { CopyToClipboard } from '../../../../components/copy-to-clipboard/copy-to-clipboard'
import { getSupportMailAddress } from '../../../../helpers/company-helpers'

interface CloneUserProps extends WithTranslation {
  appStore?: AppStore
  onClose: () => Promise<void>
  uiStore?: UiStore
  userApi?: IUserApi
  usersStore?: DashboardUsersStore
  modalLayerStore?: ModalLayerStore
  authStore?: AuthStore
}

@inject(({ appStore }: { appStore: AppStore }) => ({
  appStore,
  uiStore: appStore.uiStore,
  usersStore: appStore.usersStore,
  modalLayerStore: appStore.actionModalStore,
  authStore: appStore.authStore,
}))
@observer
class CloneUserComponent extends React.Component<CloneUserProps> {
  private store: DashboardUsersStore

  constructor(props: CloneUserProps) {
    super(props)
    this.store = props.usersStore
  }

  private handleFormSubmit = (event: React.FormEvent) => {
    const {
      store: { editUserId },
    } = this

    event.preventDefault()
    this.store.cloneUser(editUserId)
  }

  render() {
    const { onClose, t } = this.props
    const {
      store: {
        cloneLogin,
        clonePassword,
        serverError,
        firstName,
        lastName,
        cloneUserGenerated,
        cloneUserGeneratedError,
      },
    } = this
    const name = [firstName.value, lastName.value].join(' ')
    const mailAddress = getSupportMailAddress(this.props.authStore.currentUser.company)

    return (
      <div className={style.root}>
        <div className={style.subTitle}>{t('users.cloneUserTitle')}</div>
        {(firstName.value || lastName.value) && !cloneUserGenerated && (
          <div className={style.resetPasswordNoticeDesc}>
            {t('users.cloneUserNoticeDesc', { name })}
          </div>
        )}
        <div className={style.resetPasswordDesc}>
          {cloneUserGenerated ? t('users.cloneUserGeneratedDesc') : t('users.cloneUserDesc')}
        </div>
        {!cloneUserGenerated && !!cloneUserGeneratedError && (
          <div className={style.errorWarning}>
            {t('errors.errorCloningUser')}{' '}
            {!!cloneUserGeneratedError && `(${t(`errors.${cloneUserGeneratedError}`)})`}
            <div>
              {t('errors.pleaseContact')} <a href={`mailto:${mailAddress}`}>{mailAddress}</a>
            </div>
          </div>
        )}
        <div className={style.closeButton}>
          <Close onClick={onClose} />
        </div>
        <form onSubmit={this.handleFormSubmit}>
          {cloneUserGenerated && (
            <>
              <div className={style.inputWrapper}>
                <Input
                  className={style.input}
                  inputStyle={{ paddingRight: 24 }}
                  autoFocus
                  label="Username"
                  hasBeenValidated={cloneLogin.hasBeenValidated}
                  value={cloneLogin.value}
                  readOnly
                  error={cloneLogin.error || !!serverError}
                  onChange={e => cloneLogin.onChange(e.target.value)}
                />
                <CopyToClipboard
                  stringToCopy={cloneLogin.value}
                  classes={() => ({ span: style.copyToClipboard })}
                />
              </div>
              <div className={style.inputWrapper}>
                <Input
                  className={style.input}
                  inputStyle={{ paddingRight: 28 }}
                  autoFocus
                  label="Password"
                  hasBeenValidated={clonePassword.hasBeenValidated}
                  value={clonePassword.value}
                  readOnly
                  error={clonePassword.error || !!serverError}
                  onChange={e => clonePassword.onChange(e.target.value)}
                />
                <CopyToClipboard
                  stringToCopy={clonePassword.value}
                  classes={() => ({ span: style.copyToClipboard })}
                />
              </div>
            </>
          )}
          <div className={style.btnDiv}>
            {this.store.isLoadingModal && <Loader />}
            {cloneUserGenerated ? (
              <Button className={style.actionBtn} onClick={onClose}>
                {t('btn.close')}
              </Button>
            ) : (
              <Button
                className={style.actionBtn}
                type="submit"
                disabled={this.store.isLoadingModal}
              >
                {t('btn.continue')}
              </Button>
            )}
          </div>
        </form>
      </div>
    )
  }
}

export const CloneUserModal = withTranslation()(CloneUserComponent)
